import { SupportedConfigs } from '@/types/gemini';
import { getSupportedConfigs } from './api';

class GeminiConfigManager {
  private configs: SupportedConfigs | null = null;
  private loading: boolean = false;
  private listeners: Set<() => void> = new Set();

  async fetchConfigs(): Promise<SupportedConfigs> {
    if (this.loading) {
      return this.waitForConfigs();
    }

    if (this.configs) {
      return this.configs;
    }

    this.loading = true;
    try {
      const response = await getSupportedConfigs();
      this.configs = response.data;
      this.notifyListeners();
      return this.configs;
    } catch (error) {
      console.error('Failed to fetch supported configs:', error);
      this.configs = this.getDefaultConfigs();
      return this.configs;
    } finally {
      this.loading = false;
    }
  }

  getConfigs(): SupportedConfigs | null {
    return this.configs;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener());
  }

  private async waitForConfigs(): Promise<SupportedConfigs> {
    return new Promise((resolve) => {
      const unsubscribe = this.subscribe(() => {
        if (this.configs) {
          unsubscribe();
          resolve(this.configs);
        }
      });
    });
  }

  private getDefaultConfigs(): SupportedConfigs {
    return {
      models: ['gemini-2.5-flash-image', 'gemini-3-pro-image-preview'],
      aspect_ratios: {
        'gemini-2.5-flash-image': ['1:1', '2:3', '3:2', '3:4', '4:3', '4:5', '5:4', '9:16', '16:9', '21:9'],
        'gemini-3-pro-image-preview': ['1:1', '2:3', '3:2', '3:4', '4:3', '4:5', '5:4', '9:16', '16:9', '21:9']
      },
      image_sizes: {
        'gemini-2.5-flash-image': [],
        'gemini-3-pro-image-preview': ['1K', '2K', '4K']
      },
      input_image_limits: {
        'gemini-2.5-flash-image': 3,
        'gemini-3-pro-image-preview': 14
      }
    };
  }
}

export const geminiConfigManager = new GeminiConfigManager();
